import axios from 'axios';

import { AnomalyRecord } from 'data/entities/AnomalyRecord';
import { IEquipment } from 'pages/thermography/model/equipment.model';
import { IPlant } from 'pages/thermography/model/plant.model';
import { IEquipmentComponent } from 'pages/thermography/model/equipment-component.model';

import { InspectionRoute } from 'data/entities/InspectionRoute';

import { UUID } from 'crypto';
import { MapMarkerPoint, getMapMarkerPoints } from 'pages/thermography/modules/mapMarkerPoints';
import {
    parseDate
} from 'pages/thermography/Utils';
import { usePowerStationFilterStore } from './PowerStationFilterStore';
import { IUserInfo } from '../model/user-info.model';
import { IInspectionRecord } from '../model/inspection-record.model';
import { IRiskPeriodicityDeadline } from '../model/risk-periodicity-deadline.model';

export class DatabaseService {

    public isInitialized = false;
    public isPending = false;


    private static instance: DatabaseService;

    private anomalyRecords: Array<AnomalyRecord> = [];
    private anomalyRecordMonitorings: AnomalyRecord[] = [];

    private powerStations: IPlant[] = [];
    private mapMarkerPoint: MapMarkerPoint[] = [];

    private equipments: IEquipment[] = [];

    private activeAnomalyRecords: AnomalyRecord[] = [];
    private inspectionRoutes: InspectionRoute[] = [];

    private thermographicRecords: AnomalyRecord[] = [];

    private equipmentComponents: IEquipmentComponent[] = [];
    private riskPeriodicityDeadline: IRiskPeriodicityDeadline[] = [];

    private usersInfos: IUserInfo[] = [];
    private inspectionRecords: IInspectionRecord[] = [];

    private constructor() {
    }

    public static getInstance(): DatabaseService {
        if (!DatabaseService.instance) {
            DatabaseService.instance = new DatabaseService();
        }

        return DatabaseService.instance;
    }

    // Método assíncrono para carregar dados
    public async initialize(): Promise<void> {
        if (this.isInitialized) return;

        try {
            this.isPending = true;

            this.powerStations = await this.getPlantsAPI();
            this.mapMarkerPoint = getMapMarkerPoints(this.powerStations);

            this.equipments = await this.getEquipmentsAPI();
            this.equipmentComponents = await this.getComponentsAPI();
            this.riskPeriodicityDeadline = await this.getRiskPeriodicityDeadlineApi();
            
            this.inspectionRecords = await this.getInspectionRecordsApi();
            this.anomalyRecords =  await this.getAnomalyRecordsApi();

            this.usersInfos = await this.getUsersInfoApi();
            
            this.activeAnomalyRecords = [];
            this.anomalyRecordMonitorings = [];
            this.thermographicRecords = [];

            this.isPending = false;
            this.isInitialized = true;

            
            this.parseAnomalyRecords();

        } catch (error) {
            console.error('Erro ao inicializar:', error);
            this.isInitialized = false;
        }
    }

    public async getPlantsAPI() {
        try {
            const response = await axios.get('api/plants');

            console.log('Plants:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Plants:', error);
            throw error;
        }
    }

    public async getEquipmentsAPI() {
        try {
            const response = await axios.get('api/equipment');

            console.log('Equipments:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Equipamentos:', error);
            throw error;
        }
    }

    public async getComponentsAPI() {
        try {
            const response = await axios.get('api/equipment-components');

            console.log('Components:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Componentes:', error);
            throw error;
        }
    }

    public async getNewRouteAPI(plantId: String) {
        try {
            const response = await axios.get('inspection-routes/new/${plantId}');
            console.log('New Route:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Nova Rota:', error);
            throw error;
        }
    }

    public async getUsersInfoApi() {
        try {
            const response = await axios.get('api/user-infos');

            console.log('Users Infos:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Informações dos usuários:', error);
            throw error;
        }
    }

    public async getInspectionRecordsApi() {
        try {
            const response = await axios.get('api/inspection-records');

            console.log('Inspection Records:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar informações de Registros de Inspeção:', error);
            throw error;
        }
    }

    public async getAnomalyRecordsApi() {
        try {
            const response = await axios.get('api/thermographic-inspection-records');

            console.log('Anomaly Records:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar AnomalyRecords:', error);
            throw error;
        }
    }

    public async getRiskPeriodicityDeadlineApi() {
        try {
            const response = await axios.get('api/risk-periodicity-deadlines');

            console.log('Risk Periodicity Deadline:', response.data);
            return response.data;

        } catch (error) {
            console.error('Erro ao buscar Risk Periodicity Deadline:', error);
            throw error;
        }
    }

    //****************************** Power Stations ******************************//
    public getPowerStations(): IPlant[] {
        return this.powerStations;
    }

    public getMapMarkerPoints(): MapMarkerPoint[] {
        return this.mapMarkerPoint;
    }

    public getPowerStation(id: UUID): IPlant | undefined {
        return this.powerStations.find(ps => ps.id === id);
    }

    public addPowerStation(powerStation: IPlant): void {
        this.powerStations.push(powerStation);
    }

    public updatePowerStation(id: UUID, updatedPowerStation: Partial<IPlant>): boolean {
        const index = this.powerStations.findIndex(ps => ps.id === id);
        if (index === -1) {
            return false;
        }
        this.powerStations[index] = { ...this.powerStations[index], ...updatedPowerStation };
        return true;
    }

    public deletePowerStation(id: UUID): boolean {
        const index = this.powerStations.findIndex(ps => ps.id === id);
        if (index === -1) {
            return false;
        }
        this.powerStations.splice(index, 1);
        return true;
    }

    //****************************** InspectionRoutes ******************************//
    public getInspectionRoutes(): InspectionRoute[] {
        this.inspectionRoutes.sort((a, b) => {
            if (a.datetime && b.datetime) {
                if (parseDate(a.datetime).getTime() < parseDate(b.datetime).getTime()) return 1;
                if (parseDate(a.datetime).getTime() > parseDate(b.datetime).getTime()) return -1;
            }
            return 0;
        });
        return this.inspectionRoutes;
    }

    //****************************** Equipments ******************************//
    public getEquipments() {
        return this.equipments;
    }

    public getInspectionRecords() {
        return this.inspectionRecords;
    }

    //****************************** AnomalyRecord ******************************//
    public getAnomalyRecords(): AnomalyRecord[] {
        return this.anomalyRecords;
    }

    public getAnomalyRecord(id: string): AnomalyRecord | undefined {
        return this.anomalyRecords.find(ar => ar.id === id);
    }

    public getThermographers() {
        return this.usersInfos;
    }

    public async addAnomalyRecord(record: AnomalyRecord): Promise<boolean> {
        try {
            const response = await axios.post('api/thermographic-inspection-records/actions/create',
                record,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const createdRecord: AnomalyRecord = response.data;

            // Adiciona na lista local
            this.anomalyRecords.push(createdRecord);
            this.parseAnomalyRecords();
            console.log('Registro criado e adicionado na lista:', createdRecord);
            return true;

        } catch (error) {
            console.error('Erro ao criar registro:', error);
            throw error;
        }
    }

    public async updateAnomalyRecord(id: string, record: AnomalyRecord): Promise<boolean> {
        try {
            const response = await axios.post('api/thermographic-inspection-records/actions/update',
                record,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const updatedRecord: AnomalyRecord = response.data;
            console.log(`Registro ${id} atualizado:`, updatedRecord);

            const index = this.anomalyRecords.findIndex(ar => ar.id === id);
            if (index !== -1) {
                this.anomalyRecords[index] = updatedRecord;
            } else {
                // Se não estiver na lista, adiciona (opcional)
                this.anomalyRecords.push(updatedRecord);
            }

            this.parseAnomalyRecords();
            return true;

        } catch (error) {
            console.error(`Erro ao atualizar registro ${id}:`, error);
            throw error;
        }
    }

    public async deleteAnomalyRecord(id: string): Promise<boolean> {
        try {

            const response = await axios.delete(`api/thermographic-inspection-records/${id}`);

            if (response) {
                console.log(`Registro ${id} deletado:`, response.data);
                const index = this.anomalyRecords.findIndex(ar => ar.id === id);
                if (index === -1) {
                    return false;
                }
                this.anomalyRecords.splice(index, 1);
                this.parseAnomalyRecords();
                return true;
            }
            else
                return false;

        } catch (error) {
            console.error(`Erro ao deletar registro ${id}:`, error);
            throw error;
        }
    }


    //****************************** Thermographic Record ******************************//
    public getThermographicRecords(): AnomalyRecord[] {
        return this.thermographicRecords;
    }

    public getThermographicRecord(id: string): AnomalyRecord | undefined {
        return this.thermographicRecords.find(ar => ar.id === id);
    }

    public addThermographicRecord(record: AnomalyRecord): void {
        this.thermographicRecords.push(record);
    }

    public updateThermographicRecord(id: string, updatedRecord: Partial<AnomalyRecord>): boolean {
        const index = this.thermographicRecords.findIndex(ar => ar.id === id);
        if (index === -1) {
            return false;
        }
        this.thermographicRecords[index] = { ...this.thermographicRecords[index], ...updatedRecord };
        return true;
    }

    public deleteThermographicRecord(id: string): boolean {
        const index = this.thermographicRecords.findIndex(ar => ar.id === id);
        if (index === -1) {
            return false;
        }
        let copy = [...this.thermographicRecords];
        copy.splice(index, 1);
        this.thermographicRecords = copy;

        return true;
    }


    //****************************** Anomaly Record Monitoring ******************************//
    public getAnomalyRecordsMonitoring(): AnomalyRecord[] {
        return this.anomalyRecordMonitorings;
    }

    public getAnomalyRecordMonitoring(id: string): AnomalyRecord | undefined {
        return this.anomalyRecordMonitorings.find(ar => ar.id === id);
    }

    public addAnomalyRecordMonitoring(record: AnomalyRecord): void {
        this.anomalyRecordMonitorings.push(record);
    }

    public updateAnomalyRecordMonitoring(id: string, updatedRecord: Partial<AnomalyRecord>): boolean {
        const index = this.anomalyRecordMonitorings.findIndex(ar => ar.id === id);
        if (index === -1) {
            return false;
        }
        this.anomalyRecordMonitorings[index] = { ...this.anomalyRecordMonitorings[index], ...updatedRecord };
        return true;
    }

    public deleteAnomalyRecordMonitoring(id: string): boolean {
        const index = this.anomalyRecordMonitorings.findIndex(ar => ar.id === id);
        if (index === -1) {
            return false;
        }
        this.anomalyRecordMonitorings.splice(index, 1);
        return true;
    }

    //****************************** EquipamentComponenteLimits ******************************//
    public getEquipmentComponents(): IEquipmentComponent[] {
        return this.equipmentComponents;
    }

    //****************************** EquipamentComponenteLimits ******************************//
    public getRiskPeriodicityDeadline(): IRiskPeriodicityDeadline[] {
        return this.riskPeriodicityDeadline;
    }


    //******************************** Queries methods ****************************************/
    public parseAnomalyRecords(): void {
        let anomalyRecords = this.getAnomalyRecords();
        let anomalyRecordsMonitoring = this.getAnomalyRecordsMonitoring();
        this.activeAnomalyRecords = new Array<AnomalyRecord>();

        // for (const item of anomalyRecords) {
        //     let list = anomalyRecordsMonitoring.filter(x => x.anomalyRecordIdReference == item.name);
        //     if (list && list.length > 0) {
        //         list = [...list].sort((a, b) => {
        //             const dateA = a.createdAt ? parseDate(a.createdAt).getTime() : 0;
        //             const dateB = b.createdAt ? parseDate(b.createdAt).getTime() : 0;
        //             return (dateA - dateB) * -1;
        //         });
        //         let newItem = list[0];
        //         list = list.slice(1);
        //         list.push(item);
        //         this.activeAnomalyRecords.push(newItem);
        //     } else {
        //         this.activeAnomalyRecords.push(item);
        //     }
        // }

        this.activeAnomalyRecords = this.anomalyRecords.filter(x => x.condition !== 'NORMAL');
        this.activeAnomalyRecords.sort((a, b) => {
            if (a.plant && b.plant) {
                if (a.plant.id < b.plant.id) return -1;
                if (a.plant.id > b.plant.id) return 1;
            }
            if (a.createdAt && b.createdAt) {
                if (parseDate(a.createdAt).getTime() < parseDate(b.createdAt).getTime()) return 1;
                if (parseDate(a.createdAt).getTime() > parseDate(b.createdAt).getTime()) return -1;
            }
            return 0;
        });

        this.thermographicRecords = this.anomalyRecords.filter(x => x.condition === 'NORMAL');

        const store = usePowerStationFilterStore.getState();
        store.setPowerStations(this.powerStations);
        store.setAnomalyRecords(this.anomalyRecords);
        store.setActiveAnomalyRecords(this.activeAnomalyRecords);
        store.setAnomalyRecordsMonitoring(this.anomalyRecordMonitorings);
        store.setInspectionRoutes(this.inspectionRoutes);
        store.setThermographicRecords(this.thermographicRecords);
    }

    public getActiveAnomalyRecords(): AnomalyRecord[] {
        return this.activeAnomalyRecords;
    }

    public deleteActiveAnomalyRecord(id: string): boolean {
        const index = this.activeAnomalyRecords.findIndex(ar => ar.id === id);
        if (index === -1) {
            return false;
        }
        let copy = [...this.activeAnomalyRecords];
        copy.splice(index, 1);
        this.activeAnomalyRecords = copy;
        return true;
    }
}